/**
 * <SlotChangeFlow> — « Modifier mes créneaux », de bout en bout (template).
 *
 * Quel cours ne convient plus ? → une place chez son tuteur (quand le serveur en a une, pour
 * chaque cours) → sinon, ou si elle ne va pas, quels nouveaux horaires ? → si un tuteur garde un
 * autre cours de l'enfant, que faire s'il ne peut pas ? → « Confirmer votre choix ? » → l'accusé.
 * Deux issues : une place prise (« C'est fait », le changement est posé) ou une demande envoyée
 * (« Demande envoyée », les horaires proposés aux tuteurs, un autre tuteur cherché en même temps).
 *
 * Contrôlé : l'étape, les choix, l'envoi et l'issue arrivent en props, les gestes repartent en
 * callbacks. La machine vit côté écran (`useSlotChangeFlow`) — c'est ce qui rend chaque étape
 * regardable seule en story. Toutes les règles (places, couleurs des tuteurs, replis, nombre
 * d'horaires) sont celles du serveur : le gabarit les montre.
 *
 * La copie du parcours est ICI, dans ses étapes, et — pour les phrases qui se composent (le rappel
 * des cours, les lignes de « Confirmer votre choix ? » et de l'accusé) — dans `slotChangeFormatter`,
 * où elles se testent. Elle est écrite pour le parent (vouvoiement, l'enfant à la troisième
 * personne). Une étape propose au plus deux actions ; la croix ferme.
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { RecapList } from "../../molecules/RecapList"
import { SlotChangePendingDetail } from "../../molecules/SlotChangePendingDetail"
import { SlotChangeCoursesStep } from "../../organisms/SlotChangeCoursesStep"
import { SlotChangeFallbackStep } from "../../organisms/SlotChangeFallbackStep"
import { SlotChangeSeatsStep } from "../../organisms/SlotChangeSeatsStep"
import { WeekSlotGrid, type WeekSlotLegendItem } from "../../organisms/WeekSlotGrid"
import {
    type UIRecapLine,
    type UISlotChangeCourse,
    type UISlotChangeFallbackChoice,
    type UISlotChangeOptions,
    type UISlotChangeOutcome,
    type UISlotChangePending,
    type UISlotChangeSeat,
} from "../../../ports/primary/ui/types"
import { elide } from "../../../ports/primary/ui/formatters/childFormatter"
import {
    changingSlotKeys,
    chosenSeat,
    enoughSlots,
    fallbacksComplete,
    offerRecapLines,
    seatRecapLines,
    seatsComplete,
    slotChangeCoursesContext,
    slotChangeOutcomeLines,
    slotChangeStopWarning,
    tutorsWithoutFallback,
} from "../../../ports/primary/ui/formatters/slotChangeFormatter"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { formatWeeklySlot } from "../../../ports/primary/ui/formatters/weeklySlotFormatter"
import { ParentFlowModal } from "../ParentFlowModal"

export type SlotChangeFlowStep = "courses" | "seats" | "grid" | "fallback" | "done"

export type SlotChangeFlowProps = {
    visible: boolean
    onClose: () => void
    /** Absent → pas de retour (première étape, accusé). */
    onBack?: () => void
    step: SlotChangeFlowStep
    childFirstname: string

    // ── « Quel cours ne convient plus ? »
    courses: UISlotChangeCourse[]
    /** Un changement déjà en cours (le cours, les horaires proposés, où il en est) : l'étape le montre à la place des cours. */
    coursesPending?: UISlotChangePending | null
    coursesLoading?: boolean
    /** Relire les cours après un échec. */
    onRetryCourses?: () => void
    selectedCourseIds: number[]
    onToggleCourse: (liveSessionId: number) => void
    onContinueCourses: () => void
    /** Les places et la grille des cours choisis sont en route. */
    continueLoading?: boolean
    /** Ouvre « Décaler une séance » ; absent, la phrase s'écrit sans lien. */
    onMoveSession?: () => void
    /** « Pour changer de tuteur, écrivez-nous » : la messagerie, le message déjà préparé. Absent, pas de phrase. */
    onAskTutorChange?: () => void

    // ── Ce que le serveur répond pour les cours choisis
    options?: UISlotChangeOptions | null
    /** Une relecture des places (une place vient d'être prise). */
    optionsLoading?: boolean

    // ── « Une place chez … »
    seatChoices?: Record<number, number>
    onSelectSeat: (seat: UISlotChangeSeat) => void
    onAskSeatConfirm: () => void
    onDeclineSeats: () => void

    // ── « Quels nouveaux horaires ? »
    selectedSlotKeys?: string[]
    onToggleSlot: (key: string) => void
    onContinueGrid: () => void

    // ── « Si … ne peut pas ? »
    fallbackChoices?: Record<string, UISlotChangeFallbackChoice>
    onSelectFallback: (key: string, choice: UISlotChangeFallbackChoice) => void
    onContinueFallback: () => void

    // ── « Confirmer votre choix ? »
    /** La question est ouverte, sur cet envoi. */
    confirming?: "seat" | "offer" | null
    onCancelConfirm: () => void
    onConfirm: () => void
    confirmLoading?: boolean

    /** Ce que le serveur a fait — l'accusé de l'étape `done`. */
    outcome?: UISlotChangeOutcome | null
    /** Ce qui ne passe pas : une lecture en échec, une place prise entre-temps. */
    errorMessage?: string
    testID?: string
}

const TITLE = "Modifier mes créneaux"

/**
 * Les étapes que la barre compte. La question de confirmation en est une : la barre n'est jamais
 * pleine avant elle. Tant que le serveur n'a pas répondu, les étapes qui dépendent de sa réponse
 * (les places, le repli) sont comptées — la barre avance donc toujours, elle ne recule pas quand
 * une étape tombe.
 */
function progressSteps(options: UISlotChangeOptions | null): (SlotChangeFlowStep | "confirm")[] {
    return [
        "courses",
        ...(options == null || options.seats.length > 0 ? (["seats"] as const) : []),
        "grid",
        ...(options == null || options.fallbacks.length > 0 ? (["fallback"] as const) : []),
        "confirm",
    ]
}

export function SlotChangeFlow(props: SlotChangeFlowProps) {
    const {
        visible,
        onClose,
        onBack,
        step,
        childFirstname,
        courses,
        coursesPending = null,
        coursesLoading = false,
        onRetryCourses,
        selectedCourseIds,
        onToggleCourse,
        onContinueCourses,
        continueLoading = false,
        onMoveSession,
        onAskTutorChange,
        options = null,
        optionsLoading = false,
        seatChoices = {},
        onSelectSeat,
        onAskSeatConfirm,
        onDeclineSeats,
        selectedSlotKeys = [],
        onToggleSlot,
        onContinueGrid,
        fallbackChoices = {},
        onSelectFallback,
        onContinueFallback,
        confirming = null,
        onCancelConfirm,
        onConfirm,
        confirmLoading = false,
        outcome = null,
        errorMessage,
        testID,
    } = props

    const selected = selectedCourseIds
        .map((id) => courses.find((course) => course.liveSessionId === id))
        .filter((course): course is UISlotChangeCourse => course != null)
    const several = selected.length > 1
    const seats = options?.seats ?? []
    const fallbacks = options?.fallbacks ?? []
    const cancel = <Button label="Annuler" variant="outline" onPress={onClose} />
    const backToPortal = (variant: "solid" | "outline") => (
        <Button label="Revenir à l'abonnement" variant={variant} onPress={onClose} fullWidth />
    )
    const flowSteps = progressSteps(options)
    // Ce qui s'arrête : dit avant de confirmer (comme la conséquence du geste), redit dans l'accusé.
    const stopWarning = slotChangeStopWarning(selected)
    const confirmStopWarning = slotChangeStopWarning(selected, { beforeConfirm: true })

    const content = ((): {
        stepTitle: string
        context?: string
        footer?: React.ReactNode
        body: React.ReactNode
    } => {
        switch (step) {
            case "courses": {
                if (!coursesLoading && coursesPending != null) {
                    return {
                        stepTitle: "Votre demande en cours",
                        context: `pour ${childFirstname}`,
                        footer: backToPortal("outline"),
                        body: <SlotChangePendingDetail pending={coursesPending} />,
                    }
                }
                const empty = !coursesLoading && courses.length === 0
                if (empty && errorMessage == null) {
                    return {
                        stepTitle: "Quel cours ne convient plus ?",
                        context: `pour ${childFirstname}`,
                        footer: backToPortal("outline"),
                        body: <FlowOutcome iconName="calendar-alt" message="Aucun cours de la semaine à modifier." />,
                    }
                }
                return {
                    stepTitle: several ? "Quels cours ne conviennent plus ?" : "Quel cours ne convient plus ?",
                    context: `pour ${childFirstname} · ses cours de chaque semaine`,
                    footer: (
                        <Stack gap="2">
                            {empty && onRetryCourses != null ? (
                                <Button label="Réessayer" onPress={onRetryCourses} fullWidth />
                            ) : (
                                <Button
                                    label={
                                        several
                                            ? `Continuer · ${countLabel(selected.length, "cours", "cours")}`
                                            : "Continuer"
                                    }
                                    onPress={onContinueCourses}
                                    loading={continueLoading}
                                    disabled={selected.length === 0 || continueLoading}
                                    fullWidth
                                />
                            )}
                            {cancel}
                        </Stack>
                    ),
                    body: (
                        <SlotChangeCoursesStep
                            courses={courses}
                            selectedIds={selectedCourseIds}
                            onToggle={onToggleCourse}
                            loading={coursesLoading}
                            onMoveSession={onMoveSession}
                            onAskTutorChange={onAskTutorChange}
                        />
                    ),
                }
            }

            case "seats": {
                const tutorNames = [...new Set(seats.map((seat) => seat.tutor.firstname).filter((name) => name !== ""))]
                const complete = seatsComplete({ selectedCourseIds, seatChoices })
                const chosen =
                    selected.length === 1 ? chosenSeat(seats, seatChoices, selected[0].liveSessionId) : undefined
                const primary = several
                    ? "Choisir ces créneaux"
                    : chosen != null
                      ? `Choisir ${formatWeeklySlot(chosen.label, chosen.dayTime, "sentence")}`
                      : "Choisir ce créneau"
                return {
                    stepTitle:
                        tutorNames.length === 1 ? `Une place chez ${tutorNames[0]}` : "Une place chez ses tuteurs",
                    context: slotChangeCoursesContext(selected),
                    footer: (
                        <Stack gap="2">
                            <Button
                                label={primary}
                                onPress={onAskSeatConfirm}
                                disabled={!complete || optionsLoading}
                                fullWidth
                            />
                            <Button
                                label="Ça ne nous va pas, voir d'autres horaires"
                                variant="outline"
                                onPress={onDeclineSeats}
                                disabled={optionsLoading}
                                fullWidth
                            />
                        </Stack>
                    ),
                    body: (
                        <SlotChangeSeatsStep
                            courses={selected}
                            seats={seats}
                            choices={seatChoices}
                            onSelect={onSelectSeat}
                            loading={optionsLoading}
                        />
                    ),
                }
            }

            case "grid": {
                const grid = options?.grid ?? { days: [], tutors: [] }
                const minSlots = options?.minSlots ?? 1
                const count = selectedSlotKeys.length
                // Le cours qui change a sa case barrée ; « Déjà un cours » ne parle que de ceux qui restent.
                const changingKeys = changingSlotKeys(selected, grid)
                const hasCourse = grid.days.some((day) =>
                    day.slots.some((slot) => slot.course && !changingKeys.includes(slot.key))
                )
                // Une pastille : un peu plus de chances de garder le même tuteur sur ces horaires, sans plus.
                const coloured = grid.tutors.filter((tutor) => tutor.tone != null)
                const legend: WeekSlotLegendItem[] = [
                    ...coloured.map((tutor) => ({
                        key: `tutor-${tutor.id}`,
                        label:
                            coloured.length > 1 && tutor.firstname
                                ? `Plus de chances de garder ${tutor.firstname}`
                                : "Plus de chances de garder le même tuteur",
                        tone: tutor.tone,
                    })),
                    { key: "plain", label: "Autre horaire" },
                    ...(changingKeys.length > 0
                        ? [
                              {
                                  key: "changing",
                                  label: changingKeys.length > 1 ? "Les cours qui changent" : "Le cours qui change",
                                  changing: true,
                              },
                          ]
                        : []),
                    ...(hasCourse
                        ? [{ key: "course", label: `Déjà un cours ${elide(childFirstname)}`, course: true }]
                        : []),
                ]
                return {
                    stepTitle: "Quels nouveaux horaires ?",
                    context: slotChangeCoursesContext(selected, { withTutor: !several, minSlots }),
                    // Une seule action : la croix est la sortie, « Annuler » ferait un troisième bouton sous la grille.
                    footer: (
                        <Button
                            label={count > 0 ? `Continuer · ${countLabel(count, "horaire")}` : "Continuer"}
                            onPress={onContinueGrid}
                            disabled={!enoughSlots({ slotKeys: selectedSlotKeys, options })}
                            fullWidth
                        />
                    ),
                    body: (
                        <Stack gap="3">
                            <SectionLabel>Cochez les horaires qui vous conviennent</SectionLabel>
                            <WeekSlotGrid
                                days={grid.days}
                                tutors={grid.tutors}
                                selectedKeys={selectedSlotKeys}
                                onToggle={onToggleSlot}
                                changingKeys={changingKeys}
                                courseCaption="cours"
                                changingCaption="à changer"
                                legend={legend}
                                emptyMessage="Aucun horaire à proposer pour le moment."
                            />
                        </Stack>
                    ),
                }
            }

            case "fallback":
                return {
                    stepTitle:
                        fallbacks.length === 1 &&
                        fallbacks[0]?.tutor.firstname &&
                        tutorsWithoutFallback(fallbacks, selected).length === 0
                            ? `Si ${fallbacks[0].tutor.firstname} ne peut pas ?`
                            : "Si un tuteur ne peut pas ?",
                    footer: (
                        <Stack gap="2">
                            <Button
                                label="Continuer"
                                onPress={onContinueFallback}
                                disabled={!fallbacksComplete({ options, fallbackChoices })}
                                fullWidth
                            />
                            {cancel}
                        </Stack>
                    ),
                    body: (
                        <SlotChangeFallbackStep
                            fallbacks={fallbacks}
                            courses={selected}
                            choices={fallbackChoices}
                            onSelect={onSelectFallback}
                        />
                    ),
                }

            case "done":
            default: {
                // L'accusé n'a pas de question : l'en-tête ne garde que la croix, le titre est dans la page.
                const lines = outcome != null ? slotChangeOutcomeLines(outcome, childFirstname) : []
                return {
                    stepTitle: "",
                    footer: backToPortal("solid"),
                    body: (
                        <FlowOutcome
                            iconName="check"
                            tone="success"
                            center
                            heading
                            message={outcome?.kind === "offer" ? "Demande envoyée" : "C'est fait"}
                        >
                            <RecapList lines={lines} />
                            {stopWarning != null && (
                                <Banner message={stopWarning} color="danger" icon="exclamation-triangle" tinted />
                            )}
                        </FlowOutcome>
                    ),
                }
            }
        }
    })()

    const done = step === "done"
    // La demande en cours se lit sans étapes : rien à faire, pas de barre.
    const single = done || (step === "courses" && !coursesLoading && coursesPending != null)
    const recap: UIRecapLine[] =
        confirming === "seat"
            ? seatRecapLines(selected, seats, seatChoices)
            : confirming === "offer" && options != null
              ? offerRecapLines(selected, options, selectedSlotKeys, fallbackChoices)
              : []

    return (
        <>
            <ParentFlowModal
                visible={visible}
                onClose={onClose}
                title={TITLE}
                stepTitle={content.stepTitle}
                step={single ? 1 : Math.max(1, flowSteps.indexOf(step) + 1)}
                totalSteps={single ? 1 : flowSteps.length}
                onBack={done ? undefined : onBack}
                context={content.context}
                errorMessage={errorMessage}
                footer={content.footer}
                testID={testID}
            >
                {content.body}
            </ParentFlowModal>
            <ConfirmDialog
                visible={visible && confirming != null && !done}
                title="Confirmer votre choix ?"
                recap={recap}
                confirmLabel="Confirmer"
                confirmLoading={confirmLoading}
                cancelLabel="Modifier"
                danger={false}
                onConfirm={onConfirm}
                onCancel={onCancelConfirm}
            >
                {confirmStopWarning != null && (
                    <Banner message={confirmStopWarning} color="danger" icon="exclamation-triangle" tinted />
                )}
            </ConfirmDialog>
        </>
    )
}
